import { ref, computed, watch, onBeforeMount, onMounted, onUnmounted, inject } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'
import { Capacitor } from '@capacitor/core'
import { App } from '@capacitor/app'
import { SocialLogin } from '@capgo/capacitor-social-login'
import { StatusBar, Style } from '@capacitor/status-bar'
import { useAuthStore } from '@/store/modules/auth'
import { useContextStore } from '@/store/modules/context'
import { useResourcesStore } from '@/store/modules/resources'
import { getConfig, getMessages } from '@/services/client.service'
import { getContextList } from '@/services/conductor.service'
import { initialiseSession } from '@/utils/session.util'
import { useAuth } from '@/composables/useAuth'
import { bufferLog, clearPendingDeepLink, dumpAndClearLogs, getPendingDeepLink } from '@/utils/debugBuffer'
import { darkModeProviderKey, DarkModeProvider, themeProviderKey, ThemeProvider } from '@/utils/theme.util'
import { closeAllModalsEventBus } from '@/utils/eventBus'

export function useAppInitialization() {
  const store = useStore()
  const route = useRoute()
  const router = useRouter()
  const authStore = useAuthStore()
  const contextStore = useContextStore()
  const resourcesStore = useResourcesStore()
  const { checkLogin, startPeriodicCheck, stopPeriodicCheck } = useAuth()

  const { isDark } = inject(darkModeProviderKey) as DarkModeProvider
  const { updateTheme } = inject(themeProviderKey) as ThemeProvider

  const isLoading = ref(true)
  const isMobile = ref(false)
  const splashMode = ref(import.meta.env.VITE_SPLASH_MODE ?? '')
  const showSplash = ref(Capacitor.isNativePlatform())
  const MOBILE_BREAKPOINT = 830
  const processedTokens = new Set<string>()

  const showLoading = computed(() => isLoading.value || resourcesStore.loading)

  function updateBrandName() {
    if (route.path.startsWith('/betpic')) {
      store.commit('settingsModule/setBrandName', 'BetPic')
      updateTheme('betpic')
    } else if (route.path.startsWith('/bet')) {
      store.commit('settingsModule/setBrandName', 'Ask Bookmaker')
      updateTheme('askbookmaker')
    } else if (route.path.startsWith('/odds')) {
      store.commit('settingsModule/setBrandName', 'Ask Odds')
      updateTheme('askodds')
    } else {
      store.commit('settingsModule/setBrandName', 'DevMode')
      updateTheme('default')
    }
  }

  async function handleDeepLink(data: { url: string }) {
    console.info('[App] handleDeepLink entry with:', data?.url);
    if (!data || !data.url) {
      await bufferLog('[App] handleDeepLink called with empty URL');
      return;
    }

    try {
      const incomingUrl = new URL(data.url);

      if (incomingUrl.pathname.includes('/s/') || incomingUrl.searchParams.has('shareId') || incomingUrl.hash.includes('shareId')) {
        await bufferLog('[App] Share deep link detected, closing all modals');
        closeAllModalsEventBus.emit();
      }

      const protocol = incomingUrl.protocol.toLowerCase();
      const host = incomingUrl.host.toLowerCase();
      const token = incomingUrl.searchParams.get('token');

      if (token) {
        if (processedTokens.has(token)) return;
        processedTokens.add(token);
        setTimeout(() => processedTokens.delete(token!), 10000);
      }

      await bufferLog('[App] Deep Link: ' + data.url);
      
      if (protocol.startsWith('voxbet-')) {
        await bufferLog(`[App] Custom Scheme detected: ${protocol} host: ${host} path: ${incomingUrl.pathname}`);
      }

      if (incomingUrl.pathname.includes('/s/')) {
        await bufferLog('[App] Processing legacy share link');

				let linkIsValid = false
        const res = await fetch(data.url);
				if (res.ok) {
					linkIsValid = true
				}

				await bufferLog("[App] linkIsValid: " + linkIsValid)
				
				let match = null
				
				if( linkIsValid ) {
					const htmlText = await res.text();

					match = htmlText.match(/<meta property="og:url" content="(.+?)"/i);
					if (!match) {
						match = htmlText.match(/<meta http-equiv="refresh" content="0;url=(.+?)"/i);
					}
					if (!match) {
						match = htmlText.match(/<meta property="voxbet:deeplink" content="(.+?)"/i);
					}
					if (!match) {
						match = htmlText.match(/<a href="(.+?)">Redirect now<\/a>/i);
					}
				}

        const finalUrl = match ? match[1] : data.url;
        const redirectUrl = new URL(finalUrl);

        let routePath = redirectUrl.hash.replace(/^#/, '');

				await bufferLog("[App] routePath 1: " + routePath)
				
        if (!routePath && redirectUrl.protocol.startsWith('voxbet-')) {
          routePath = redirectUrl.pathname;
          if (redirectUrl.host && redirectUrl.host !== 'qr-login' && redirectUrl.host !== 'apple-login') {
            routePath = '/' + redirectUrl.host + routePath;
          }
        }

				await bufferLog("[App] routePath 2: " + routePath)
				
        if (routePath) {
          if (import.meta.env.VITE_SPLASH_MODE === 'betpic') {
            routePath = routePath.replace(/^\/bet\//, '/betpic/')
          }
          await bufferLog('[App] Routing to: ' + routePath);
          router.push(routePath);
        } else {
					await bufferLog("[App] routePath: 4 " + routePath)
					await router.push({name: 'expiredOrNotFound'});
				}
      } else if (protocol.startsWith('voxbet-') && host === 'qr-login') {
        const token = incomingUrl.searchParams.get('token');
        await bufferLog('[App] Deep Link QR Login attempt. Host=' + host + ', token=' + (token ? 'Present' : 'Missing'));
        
        if (token) {
          isLoading.value = true;
          try {
            await authStore.qrLogin(token);
            await bufferLog('[App] QR Login successful via Deep Link');
            
            await clearPendingDeepLink();
            await router.push('/');
          } catch (err: any) {
            const errMsg = err.response?.data?.message || err.message || err;
            await bufferLog('[App] QR Login failed via Deep Link. Error: ' + errMsg, 'error');
            if (err.response) {
              await bufferLog('[App] Server Response: ' + JSON.stringify(err.response.data), 'error');
            }
          } finally {
            isLoading.value = false;
          }
        }
      } else if (protocol.startsWith('voxbet-') && host === 'apple-login') {
        const success = incomingUrl.searchParams.get('success');
        const idToken = incomingUrl.searchParams.get('id_token') || incomingUrl.searchParams.get('idToken');
        
        if (success === 'true' && idToken) {
          await bufferLog('[App] Handling Apple Login Deep Link');
          isLoading.value = true;
          try {
            await authStore.loginWithApple(idToken);
            await bufferLog('[App] Apple Login successful via Deep Link');
            await clearPendingDeepLink();
          } catch (err: any) {
            const errMsg = err.response?.data?.message || err.message || err;
            await bufferLog('[App] Apple Login failed via Deep Link. Error: ' + errMsg, 'error');
          } finally {
            isLoading.value = false;
          }
        }
      }
    } catch (err) {
      await bufferLog('[App] Failed to process deep link: ' + err, 'error');
    }
  }

  async function initializeSocialLogin() {
    const customGoogleClientId = (window as any).__APP_CONFIG__?.googleClientId
    const customGoogleRedirectUrl = (window as any).__APP_CONFIG__?.googleRedirectUrl
    const customAppleRedirectUrlWeb = (window as any).__APP_CONFIG__?.appleRedirectUrlWeb
    const customAppleRedirectUrlApp = (window as any).__APP_CONFIG__?.appleRedirectUrlApp

    let appleClientId = ''
    
    if (!Capacitor.isNativePlatform()) {
      if (route.path.startsWith('/betpic')) {
        appleClientId = ((window as any).__APP_CONFIG__?.appleClientIdBetPic || import.meta.env.VITE_APPLE_CLIENT_ID_BETPIC || '').trim()
      } else if (route.path.startsWith('/bet')) {
        appleClientId = ((window as any).__APP_CONFIG__?.appleClientIdBookmaker || import.meta.env.VITE_APPLE_CLIENT_ID_BOOKMAKER || '').trim()
      } else if (route.path.startsWith('/odds')) {
        appleClientId = ((window as any).__APP_CONFIG__?.appleClientIdOdds || import.meta.env.VITE_APPLE_CLIENT_ID_ODDS || '').trim()
      } else {
        console.log('[SocialLogin] Skipping initialization on dev screen')
        return;
      }
    } else {
      appleClientId = (import.meta.env.VITE_APPLE_CLIENT_ID || '').trim()
    }

    try {
      if (Capacitor.isNativePlatform()) {
        const appleConfig = {
          clientId: appleClientId,
          useProperTokenExchange: false,
          useBroadcastChannel: false,
          responseMode: 'form_post',
          ...(Capacitor.getPlatform() === 'android' ? { 
            redirectUrl: (import.meta.env.VITE_APPLE_REDIRECT_URL || customAppleRedirectUrlApp || '').trim() 
          } : {})
        }
        await SocialLogin.initialize({
          google: {
            webClientId: customGoogleClientId || import.meta.env.VITE_GOOGLE_CLIENT_ID,
            iOSClientId: import.meta.env.VITE_GOOGLE_IOS_ID,
            iOSServerClientId: customGoogleClientId || import.meta.env.VITE_GOOGLE_CLIENT_ID,
            mode: 'online'
          },
          apple: appleConfig
        })
      } else {
        const brand = (route.path.startsWith('/betpic') ? 'betpic' : route.path.startsWith('/bet') ? 'direct' : 'odds')
        const googleBrand = 'web'
        const appleConfig = {
          clientId: appleClientId,
          useProperTokenExchange: true,
          useBroadcastChannel: false,
          responseMode: 'form_post',
          redirectUrl: (customAppleRedirectUrlWeb || import.meta.env.VITE_APPLE_REDIRECT_URL || '').replace('{brand}', brand).trim()
        }
        const googleRedirectUrl = (import.meta.env.VITE_GOOGLE_REDIRECT_URL || customGoogleRedirectUrl || '').replace('{brand}', googleBrand).trim()
        
        await SocialLogin.initialize({
          apple: appleConfig,
          google: {
            webClientId: customGoogleClientId || import.meta.env.VITE_GOOGLE_CLIENT_ID,
            mode: 'offline',
            redirectUrl: googleRedirectUrl
          }
        })
      }
    } catch (err) {
      console.error('[SocialLogin] Initialization failed', err)
    }
  }

  function checkMobile() {
    isMobile.value = window.innerWidth < MOBILE_BREAKPOINT
    if (isMobile.value) {
      document.body.classList.add('is-mobile')
    } else {
      document.body.classList.remove('is-mobile')
    }
  }

  function checkNativePlatform() {
    if (Capacitor.isNativePlatform()) {
      document.body.classList.add('is-native')
    } else {
      document.body.classList.remove('is-native')
    }
  }

  watch(isDark, async (newIsDark) => {
    if (Capacitor.isNativePlatform()) {
      await StatusBar.setStyle({ style: newIsDark ? Style.Dark : Style.Light });
    }
  }, { immediate: true })

  watch(() => route.path, async (newPath, oldPath) => {
    updateBrandName()
    
    const isBuilder = (path: string) => path.startsWith('/betpic') || path.startsWith('/bet') || path.startsWith('/odds')
    if (isBuilder(newPath) && (!oldPath || !isBuilder(oldPath))) {
      await initializeSocialLogin()
    } else if (isBuilder(newPath) && oldPath && isBuilder(oldPath)) {
      const wasOdds = oldPath.startsWith('/odds')
      const isOdds = newPath.startsWith('/odds')
      if (wasOdds !== isOdds) {
        await initializeSocialLogin()
      }
    }
  })

  const stbApiHost = computed(() => store.getters['configModule/stbApiHost'])

  watch(stbApiHost, async (newHost) => {
    if (newHost && newHost !== '') {
      try {
        const contextList = await getContextList()
        contextStore.setContextMap(contextList.contexts)
      } catch (error) {
        console.error('Failed to load context list:', error)
      }
    }
  }, { immediate: true })

  onBeforeMount(async () => {
    try {
      if (Capacitor.isNativePlatform()) {
        const launchUrl = await App.getLaunchUrl();
        if (launchUrl && launchUrl.url) {
          await handleDeepLink(launchUrl);
        } else {
          const pendingUrl = await getPendingDeepLink();
          if (pendingUrl) {
            await handleDeepLink({ url: pendingUrl });
          }
        }
      }

      const appRootUrl = `${window.location.protocol}//${window.location.host}${window.location.pathname}`
      const [configResponse, messagesResponse] = await Promise.all([
        getConfig(),
        getMessages(),
      ])

      await Promise.all([
        store.dispatch('configModule/setAppRootUrl', appRootUrl),
        store.dispatch('configModule/setStbApiHost', configResponse.stbApiHost),
        store.dispatch('configModule/setAppClientId', configResponse.appClientId),
        store.dispatch('configModule/setAuthServerBaseUrl', configResponse.authServerBaseUrl),
        store.dispatch('configModule/setStaticBaseUrl', configResponse.staticBaseUrl),
        store.dispatch('configModule/setOperationMode', configResponse.operationMode),
        store.dispatch('configModule/setUIOptions', configResponse.uiOptions),
        store.dispatch('configModule/setAppClientHome', configResponse.appClientHome),
        store.dispatch('configModule/setStbApiIsSecure', configResponse.stbApiIsSecure ?? true),
        store.dispatch('configModule/setMessages', messagesResponse.messages),
        store.dispatch('configModule/setAuthToken', configResponse.authToken),
        store.dispatch('configModule/setThemes', configResponse.themes)
      ])

      await initialiseSession()
      await checkLogin()
    } catch (error: any) {
      console.error('Failed to initialize app config:', error)
      if (error.response?.status !== 401) {
        const currentPath = window.location.hash || window.location.pathname
        const mode = currentPath.includes('betpic') ? 'betpic' : currentPath.includes('odds') ? 'odds' : store.getters['configModule/operationMode'] || import.meta.env.VITE_SPLASH_MODE
        const prefix = mode === 'betpic' ? '/betpic' : mode === 'odds' ? '/odds' : '/bet'
        await router.push(`${prefix}/no-connection`)
      }
    } finally {
      isLoading.value = false
    }
  })

  onMounted(async () => {
    checkMobile()
    checkNativePlatform()
    window.addEventListener('resize', checkMobile)
    
    App.addListener('appUrlOpen', handleDeepLink)

    try {
      await resourcesStore.loadResources()
      await bufferLog('✅ SVG resources loaded')
    } catch (err) {
      await bufferLog('❌ Failed to load SVG resources: ' + err, 'error')
    }

    await initializeSocialLogin()
    startPeriodicCheck()

    if (Capacitor.isNativePlatform()) {
      setTimeout(async () => {
        await dumpAndClearLogs();
      }, 2000);
    }
  })

  onUnmounted(() => {
    window.removeEventListener('resize', checkMobile)
    stopPeriodicCheck()
    App.removeAllListeners()
  })

  // Initial call
  updateBrandName()

  return {
    isLoading,
    showLoading,
    isMobile,
    splashMode,
    showSplash,
    processedTokens
  }
}
